import { CategorisedStaticBenefits } from '@type/job-add';
import { Input, RecordInterface } from '@components/common/Forms';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

/** Props */
interface EmployerBenefitsListProps {
  benefits?: CategorisedStaticBenefits[],
  record: RecordInterface,
  className?: string,
  inputName?: string,
  type?: 'form' | 'list',
}

/**
 * @description Employer benefits list component.
 * - `benefits` - The benefits list.
 * - `record` - record function from useBjForm
 * - `displayAs` - Display as form or list (hidden form).
 * - `className` - Add your own classes to the component. It replaces the default classes.
 * - `inputName` - The name of the input field.
 * - `type` - The type of the component. Default is form. If 'list', form elements will be hidden.
 */
export const EmployerBenefitsList = (props: EmployerBenefitsListProps) => {
  // Destructure props
  const {
    benefits,
    record,
    className,
    type = 'form',
    inputName = 'benefits',
  } = props;

  // Don't render if no benefits
  if (!benefits) return null;

  // Render component
  // *************************
  return (
    <div className={className || 'grid grid-cols-1 gap-6 text-sm sm:grid-cols-2 lg:grid-cols-3'}>
      {benefits.map((group) => (
        <div key={group.category} {...(type === 'list' && group?.hidden ? { className: 'hidden' } : {})}>
          {/* Group title */}
          <div className="flex items-center gap-1.5">
            <span className="-mt-0.5">
              {group.icon}
            </span>
            <h6 className="font-bold">
              {group.category}
            </h6>
          </div>

          {/* Group benefits */}
          {group?.benefits?.map((benefit) => {
            const isHidden = type === 'list' && benefit?.hidden;
            const withIcon = type === 'list' && !benefit?.hidden;
            return (
              <label
                htmlFor={`${inputName}-${benefit?.id}`}
                className={`mt-2 flex items-start gap-2${isHidden ? ' hidden' : ''}${!withIcon ? ' cursor-pointer' : ''}`}
                key={benefit?.id}
              >
                {withIcon && (
                  <CheckCircleIcon
                    className="size-4.5 shrink-0 text-ink-medium"
                  />
                )}
                <Input
                  type="checkbox"
                  className={`mt-0.5${withIcon ? ' hidden' : ''}`}
                  {...record(inputName, {
                    id: `${inputName}-${benefit?.id}`,
                    defaultValue: benefit?.id,
                    convertValue: 'toNumber',
                    valueAsArray: true,
                  })}
                />
                <span className="text-ink-medium">
                  {benefit?.name}
                </span>
              </label>
            );
          })}
        </div>
      ))}
    </div>
  );
};
